import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Edit2, Trash2, Check, X, AlertTriangle, Loader2,
  Play, Eye, ChevronLeft, ScrollText, RefreshCw, CheckCircle, XCircle,
  AlignLeft, AlignJustify
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { format } from 'sql-formatter';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import systemRuleMaintenanceService from '../../../../services/systemRuleMaintenanceService';
import {
  SystemRuleMaintenanceDto,
  CreateSystemRuleRequest,
  UpdateSystemRuleRequest,
  RuleFunctionTypeDto,
  SqlPreviewResult,
  SystemRuleFormData,
  emptyFormData,
} from '../../../../types/systemRuleMaintenance';

interface SystemRuleMaintenanceProps {
  onBack?: () => void;
}

type ModalMode = 'create' | 'edit' | 'view' | null;
type ModalTab = 'details' | 'preview';

export const SystemRuleMaintenance: React.FC<SystemRuleMaintenanceProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // List state
  const [rules, setRules] = useState<SystemRuleMaintenanceDto[]>([]);
  const [filteredRules, setFilteredRules] = useState<SystemRuleMaintenanceDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyUserRules, setShowOnlyUserRules] = useState(false);

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [modalTab, setModalTab] = useState<ModalTab>('details');
  const [selectedRule, setSelectedRule] = useState<SystemRuleMaintenanceDto | null>(null);
  const [formData, setFormData] = useState<SystemRuleFormData>(emptyFormData);
  const [functionTypes, setFunctionTypes] = useState<RuleFunctionTypeDto[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // SQL validation/preview state
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; message: string } | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<SqlPreviewResult | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<SystemRuleMaintenanceDto | null>(null);

  // Load rules
  const loadRules = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await systemRuleMaintenanceService.getAll();
      setRules(data);
      setFilteredRules(data);
    } catch (err: any) {
      setError(err.message || t('manager.modules.systemRules.errors.loadFailed', {}, 'Kunne ikke hente regler'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Load function types
  const loadFunctionTypes = useCallback(async () => {
    try {
      const types = await systemRuleMaintenanceService.getFunctionTypes();
      setFunctionTypes(types);
    } catch (err) {
      console.error('Failed to load function types:', err);
    }
  }, []);

  useEffect(() => {
    loadRules();
    loadFunctionTypes();
  }, [loadRules, loadFunctionTypes]);

  // Filter rules when search term or filter changes
  useEffect(() => {
    let filtered = rules;

    if (showOnlyUserRules) {
      filtered = filtered.filter(r => r.ruleType === 1);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        r =>
          r.lng1.toLowerCase().includes(term) ||
          (r.lng2 && r.lng2.toLowerCase().includes(term)) ||
          (r.sqlExpression && r.sqlExpression.toLowerCase().includes(term)) ||
          r.ruleID.toString().includes(term)
      );
    }

    setFilteredRules(filtered);
  }, [rules, searchTerm, showOnlyUserRules]);

  // Open modal for create
  const handleCreate = () => {
    setSelectedRule(null);
    setFormData(emptyFormData);
    setModalMode('create');
    setModalTab('details');
    setFormError(null);
    setValidationResult(null);
    setPreviewResult(null);
  };

  // Open modal for edit
  const handleEdit = (rule: SystemRuleMaintenanceDto) => {
    if (rule.ruleType !== 1) {
      return; // Can only edit user rules
    }
    setSelectedRule(rule);
    setFormData({
      lng1: rule.lng1,
      lng2: rule.lng2 || '',
      sqlExpression: rule.sqlExpression || '',
      ruleFunctionType: rule.ruleFunctionType,
      activeYesNo: rule.activeYesNo,
    });
    setModalMode('edit');
    setModalTab('details');
    setFormError(null);
    setValidationResult(null);
    setPreviewResult(null);
  };

  // Open modal for view (read-only)
  const handleView = (rule: SystemRuleMaintenanceDto) => {
    setSelectedRule(rule);
    setFormData({
      lng1: rule.lng1,
      lng2: rule.lng2 || '',
      sqlExpression: rule.sqlExpression || '',
      ruleFunctionType: rule.ruleFunctionType,
      activeYesNo: rule.activeYesNo,
    });
    setModalMode('view');
    setModalTab('details');
    setFormError(null);
    setValidationResult(null);
    setPreviewResult(null);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedRule(null);
    setFormData(emptyFormData);
    setFormError(null);
    setValidationResult(null);
    setPreviewResult(null);
  };

  // Save rule
  const handleSave = async () => {
    if (!formData.lng1.trim()) {
      setFormError(t('manager.modules.systemRules.form.ruleNameRequired', {}, 'Regelnavn er påkrevd'));
      return;
    }
    if (!formData.sqlExpression.trim()) {
      setFormError(t('manager.modules.systemRules.form.sqlRequired', {}, 'SQL-uttrykk er påkrevd'));
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      if (modalMode === 'create') {
        const request: CreateSystemRuleRequest = {
          lng1: formData.lng1,
          lng2: formData.lng2 || undefined,
          sqlExpression: formData.sqlExpression,
          ruleFunctionType: formData.ruleFunctionType,
          activeYesNo: formData.activeYesNo,
        };
        await systemRuleMaintenanceService.create(request);
      } else if (modalMode === 'edit' && selectedRule) {
        const request: UpdateSystemRuleRequest = {
          lng1: formData.lng1,
          lng2: formData.lng2 || undefined,
          sqlExpression: formData.sqlExpression,
          ruleFunctionType: formData.ruleFunctionType,
          activeYesNo: formData.activeYesNo,
        };
        await systemRuleMaintenanceService.update(selectedRule.ruleID, request);
      }

      await loadRules();
      handleCloseModal();
    } catch (err: any) {
      setFormError(err.message || t('manager.modules.systemRules.errors.saveFailed', {}, 'Kunne ikke lagre regel'));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete rule
  const handleDeleteClick = (rule: SystemRuleMaintenanceDto) => {
    if (rule.ruleType !== 1) {
      return; // Can only delete user rules
    }
    setRuleToDelete(rule);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    setIsDeleting(true);
    try {
      await systemRuleMaintenanceService.delete(ruleToDelete.ruleID);
      await loadRules();
      setShowDeleteConfirm(false);
      setRuleToDelete(null);
    } catch (err: any) {
      setFormError(err.message || t('manager.modules.systemRules.errors.deleteFailed', {}, 'Kunne ikke slette regel'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Validate SQL
  const handleValidateSql = async () => {
    if (!formData.sqlExpression.trim()) {
      setValidationResult({ isValid: false, message: t('manager.modules.systemRules.sql.empty', {}, 'SQL-uttrykk er tomt') });
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const result = await systemRuleMaintenanceService.validateSql(formData.sqlExpression);
      setValidationResult({
        isValid: result.isValid,
        message: result.isValid
          ? t('manager.modules.systemRules.sql.valid', {}, 'SQL-syntaks er gyldig')
          : result.errorMessage || t('manager.modules.systemRules.sql.invalid', {}, 'Ugyldig SQL-syntaks'),
      });
    } catch (err: any) {
      setValidationResult({ isValid: false, message: err.message || t('manager.modules.systemRules.sql.validationFailed', {}, 'Validering feilet') });
    } finally {
      setIsValidating(false);
    }
  };

  // Preview SQL
  const handlePreviewSql = async () => {
    if (!formData.sqlExpression.trim()) {
      return;
    }

    setIsPreviewing(true);
    setPreviewResult(null);
    setModalTab('preview');

    try {
      const result = await systemRuleMaintenanceService.previewSql(formData.sqlExpression, 10);
      setPreviewResult(result);
    } catch (err: any) {
      setPreviewResult({
        success: false,
        errorMessage: err.message || t('manager.modules.systemRules.sql.previewFailed', {}, 'Forhåndsvisning feilet'),
        affectedRowCount: 0,
        previewRows: [],
        columnNames: [],
        convertedSelectQuery: null,
      });
    } finally {
      setIsPreviewing(false);
    }
  };

  // Format SQL with sql-formatter
  const handleFormatSql = (compact: boolean = false) => {
    try {
      let sql = formData.sqlExpression;

      if (compact) {
        // Kompakt formatering - minimal innrykk og mellomrom
        const formatted = format(sql, {
          language: 'tsql',
          keywordCase: 'upper',
          tabWidth: 2,
          useTabs: false,
          denseOperators: true,
          linesBetweenQueries: 0,
        });

        // Gjør den enda mer kompakt ved å fjerne unødvendige linjeskift
        // Behold SELECT, FROM, WHERE, JOIN etc. på egne linjer, men fjern andre
        const lines = formatted.split('\n');
        const compactLines: string[] = [];
        let currentLine = '';

        const majorKeywords = ['SELECT', 'FROM', 'WHERE', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'CROSS JOIN', 'JOIN', 'ON', 'AND', 'OR', 'GROUP BY', 'HAVING', 'ORDER BY', 'UNION', 'UPDATE', 'SET', 'DELETE', 'INSERT', 'VALUES'];

        lines.forEach((line, idx) => {
          const trimmed = line.trim();
          if (!trimmed) return;

          // Sjekk om linjen starter med et major keyword
          const startsWithKeyword = majorKeywords.some(kw =>
            trimmed.toUpperCase().startsWith(kw + ' ') || trimmed.toUpperCase() === kw
          );

          if (startsWithKeyword && currentLine) {
            compactLines.push(currentLine.trim());
            currentLine = trimmed;
          } else if (startsWithKeyword) {
            currentLine = trimmed;
          } else {
            // Legg til på samme linje
            currentLine = currentLine ? currentLine + ' ' + trimmed : trimmed;
          }
        });

        if (currentLine) {
          compactLines.push(currentLine.trim());
        }

        sql = compactLines.join('\n');
      } else {
        // Standard formatering - fullt innrykk
        sql = format(sql, {
          language: 'tsql',
          keywordCase: 'upper',
          tabWidth: 4,
          useTabs: false,
          linesBetweenQueries: 1,
        });
      }

      setFormData(prev => ({ ...prev, sqlExpression: sql }));
    } catch (err) {
      console.error('SQL formatting failed:', err);
      // Hvis formatering feiler, la SQL være uendret
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('common.buttons.back', {}, 'Tilbake')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <ScrollText className="w-6 h-6 text-gray-600" />
              <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.systemRules.header.title', {}, 'Regler')}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadRules}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.buttons.refresh', {}, 'Oppdater')}
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('manager.modules.systemRules.header.newRule', {}, 'Ny regel')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('manager.modules.systemRules.filters.searchPlaceholder', {}, 'Søk etter regler...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyUserRules}
              onChange={e => setShowOnlyUserRules(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{t('manager.modules.systemRules.filters.showOnlyUserRules', {}, 'Vis kun brukerregler')}</span>
          </label>

          <span className="text-sm text-gray-500">
            {t('manager.modules.systemRules.filters.count', { '0': filteredRules.length, '1': rules.length }, `${filteredRules.length} av ${rules.length} regler`)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.fields.name', {}, 'Navn')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.fields.type', {}, 'Type')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.fields.status', {}, 'Status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRules.table.lastUpdated', {}, 'Sist oppdatert')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRules.table.actions', {}, 'Handlinger')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRules.map(rule => (
                  <tr
                    key={rule.ruleID}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => (rule.ruleType === 1 ? handleEdit(rule) : handleView(rule))}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{rule.ruleID}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{rule.lng1}</div>
                      {rule.lng2 && <div className="text-xs text-gray-500">{rule.lng2}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          rule.ruleType === 1
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {rule.ruleType === 1
                          ? t('manager.modules.systemRules.ruleType.user', {}, 'Bruker')
                          : t('manager.modules.systemRules.ruleType.system', {}, 'System')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {rule.activeYesNo ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {t('manager.modules.systemRules.status.active', {}, 'Aktiv')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                          <XCircle className="w-4 h-4" />
                          {t('manager.modules.systemRules.status.inactive', {}, 'Inaktiv')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {rule.lastUpdate
                        ? new Date(rule.lastUpdate).toLocaleString('nb-NO')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                        {rule.ruleType === 1 ? (
                          <>
                            <button
                              onClick={() => handleEdit(rule)}
                              className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                              title={t('common.buttons.edit', {}, 'Rediger')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(rule)}
                              className="p-1.5 hover:bg-red-100 rounded text-red-600"
                              title={t('common.buttons.delete', {}, 'Slett')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleView(rule)}
                            className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                            title={t('common.buttons.view', {}, 'Vis')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      {t('manager.modules.systemRules.table.noRules', {}, 'Ingen regler funnet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create'
                  ? t('manager.modules.systemRules.modal.createTitle', {}, 'Ny regel')
                  : modalMode === 'edit'
                  ? t('manager.modules.systemRules.modal.editTitle', {}, 'Rediger regel')
                  : t('manager.modules.systemRules.modal.viewTitle', {}, 'Vis regel')}
                {selectedRule && ` (ID: ${selectedRule.ruleID})`}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Tabs */}
            <div className="flex border-b border-gray-200 px-6">
              <button
                onClick={() => setModalTab('details')}
                className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  modalTab === 'details'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('manager.modules.systemRules.modal.tabDetails', {}, 'Detaljer')}
              </button>
              <button
                onClick={() => setModalTab('preview')}
                className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  modalTab === 'preview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('manager.modules.systemRules.modal.tabPreview', {}, 'Forhåndsvisning')}
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{formError}</span>
                </div>
              )}

              {modalTab === 'details' ? (
                <div className="space-y-4">
                  {/* Rule Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('manager.modules.systemRules.form.ruleNameNo', {}, 'Regelnavn (norsk)')} *
                      </label>
                      <input
                        type="text"
                        value={formData.lng1}
                        onChange={e => setFormData(prev => ({ ...prev, lng1: e.target.value }))}
                        disabled={modalMode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        placeholder={t('manager.modules.systemRules.form.ruleNamePlaceholder', {}, 'Skriv inn regelnavn...')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('manager.modules.systemRules.form.ruleNameEn', {}, 'Regelnavn (engelsk)')}
                      </label>
                      <input
                        type="text"
                        value={formData.lng2}
                        onChange={e => setFormData(prev => ({ ...prev, lng2: e.target.value }))}
                        disabled={modalMode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                        placeholder={t('manager.modules.systemRules.form.ruleNameEnPlaceholder', {}, 'Enter rule name...')}
                      />
                    </div>
                  </div>

                  {/* Function Type and Active */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('manager.modules.systemRules.form.functionType', {}, 'Funksjonstype')}
                      </label>
                      <select
                        value={formData.ruleFunctionType}
                        onChange={e => setFormData(prev => ({ ...prev, ruleFunctionType: parseInt(e.target.value) }))}
                        disabled={modalMode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                      >
                        {functionTypes.map(ft => (
                          <option key={ft.id} value={ft.id}>
                            {ft.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.activeYesNo}
                          onChange={e => setFormData(prev => ({ ...prev, activeYesNo: e.target.checked }))}
                          disabled={modalMode === 'view'}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{t('manager.modules.systemRules.status.active', {}, 'Aktiv')}</span>
                      </label>
                    </div>
                  </div>

                  {/* SQL Expression Editor */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('manager.modules.systemRules.form.sqlExpression', {}, 'SQL-uttrykk')} *
                      </label>
                      {modalMode !== 'view' && (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center border border-gray-300 rounded overflow-hidden">
                            <button
                              onClick={() => handleFormatSql(true)}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 border-r border-gray-300"
                              title={t('manager.modules.systemRules.sql.compactTooltip', {}, 'Kompakt formatering - mindre innrykk')}
                            >
                              <AlignJustify className="w-3 h-3" />
                              {t('manager.modules.systemRules.sql.compact', {}, 'Kompakt')}
                            </button>
                            <button
                              onClick={() => handleFormatSql(false)}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                              title={t('manager.modules.systemRules.sql.standardTooltip', {}, 'Standard formatering - fullt innrykk')}
                            >
                              <AlignLeft className="w-3 h-3" />
                              {t('manager.modules.systemRules.sql.standard', {}, 'Standard')}
                            </button>
                          </div>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={handleValidateSql}
                            disabled={isValidating}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                          >
                            {isValidating ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                            {t('manager.modules.systemRules.sql.validate', {}, 'Valider')}
                          </button>
                          <button
                            onClick={handlePreviewSql}
                            disabled={isPreviewing}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                          >
                            {isPreviewing ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                            {t('manager.modules.systemRules.sql.preview', {}, 'Forhåndsvis')}
                          </button>
                        </div>
                      )}
                    </div>

                    {validationResult && (
                      <div
                        className={`mb-2 p-2 rounded text-sm flex items-center gap-2 ${
                          validationResult.isValid
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {validationResult.isValid ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        {validationResult.message}
                      </div>
                    )}

                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                      <Editor
                        height="300px"
                        defaultLanguage="sql"
                        value={formData.sqlExpression}
                        onChange={value => setFormData(prev => ({ ...prev, sqlExpression: value || '' }))}
                        options={{
                          readOnly: modalMode === 'view',
                          minimap: { enabled: false },
                          fontSize: 13,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          wordWrap: 'on',
                          automaticLayout: true,
                        }}
                        theme="vs-light"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Preview Tab */
                <div>
                  {isPreviewing ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : previewResult ? (
                    <div className="space-y-4">
                      {previewResult.success ? (
                        <>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="text-sm text-blue-800">
                              <strong>{t('manager.modules.systemRules.preview.affectedRows', {}, 'Antall rader som vil bli påvirket:')}</strong> {previewResult.affectedRowCount}
                            </div>
                          </div>

                          {previewResult.convertedSelectQuery && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('manager.modules.systemRules.preview.convertedQuery', {}, 'Konvertert SELECT-spørring:')}</h4>
                              <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto">
                                {previewResult.convertedSelectQuery}
                              </pre>
                            </div>
                          )}

                          {previewResult.previewRows.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">
                                {t('manager.modules.systemRules.preview.rowsHeader', {}, 'Forhåndsvisning av rader (maks 10):')}
                              </h4>
                              <div className="border border-gray-200 rounded-lg overflow-auto max-h-64">
                                <table className="w-full text-xs">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      {previewResult.columnNames.map(col => (
                                        <th
                                          key={col}
                                          className="px-3 py-2 text-left font-medium text-gray-600 border-b"
                                        >
                                          {col}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {previewResult.previewRows.map((row, idx) => (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        {previewResult.columnNames.map(col => (
                                          <td key={col} className="px-3 py-2 text-gray-700 font-mono">
                                            {String(row[col] ?? '')}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                          <AlertTriangle className="w-5 h-5" />
                          <span>{previewResult.errorMessage}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t('manager.modules.systemRules.preview.emptyHint', {}, 'Klikk "Forhåndsvis" for å se hvilke rader som vil bli påvirket')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {modalMode !== 'view' && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {isSaving ? t('common.status.saving', {}, 'Lagrer...') : t('common.buttons.save', {}, 'Lagre')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && ruleToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.systemRules.delete.title', {}, 'Bekreft sletting')}</h3>
            </div>

            <p className="text-gray-600 mb-6">
              {t('manager.modules.systemRules.delete.confirmMessage', { '0': ruleToDelete.lng1 }, `Er du sikker på at du vil slette regelen "${ruleToDelete.lng1}"? Denne handlingen kan ikke angres.`)}
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setRuleToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 transition-colors"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isDeleting ? t('manager.modules.systemRules.delete.deleting', {}, 'Sletter...') : t('common.buttons.delete', {}, 'Slett')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemRuleMaintenance;
